import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import DOMPurify from 'dompurify';
import { Button, PageShell, Skeleton, toast } from '@/components/ui';
import { PledgeMemberButton, usePledgeMembers } from '../components/FundingPledgeMember';
import { ApiClientError } from '@/lib/http-core';
import type { AdminRole, Funding, FundingStatusEnum, Money, PledgeStatusEnum } from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import { canPerformFundingWrite } from '../lib/fundingActionRbac';
import { useFundingCampaignQuery } from '../hooks/useFundingCampaignQuery';
import { useFundingPledgesQuery } from '../hooks/useFundingPledgesQuery';
import { useFundingStatisticsQuery } from '../hooks/useFundingStatisticsQuery';
import { useVendorNameMap } from '../hooks/useVendorNameMap';
import {
  useCloseFundingCampaignMutation,
  useDeleteFundingCampaignMutation,
  useLaunchFundingCampaignMutation,
} from '../hooks/useFundingMutation';
import { FundingStatusPill } from '../components/FundingStatusPill';
import { PledgeStatusPill } from '../components/PledgeStatusPill';
import { RewardManager } from '../components/RewardManager';
import { FundingLaunchModal } from '../components/FundingLaunchModal';
import { GOAL_UNSET_LABEL, isGoalUnset } from '../lib/fundingGoal';
import { FundingCloseModal } from '../components/FundingCloseModal';
import { FundingDeleteModal } from '../components/FundingDeleteModal';
import { FundingStatsCard } from '../components/FundingStatsCard';

const DELETABLE_STATUSES: ReadonlyArray<FundingStatusEnum> = ['DRAFT', 'CANCELLED'];

const isDeletable = (status: FundingStatusEnum): boolean =>
  DELETABLE_STATUSES.includes(status);

type Tab = 'overview' | 'rewards' | 'pledges' | 'statistics';

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

const formatMoney = (money: Money): string =>
  `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;

// D-6: 응답 DTO 의 vendorName/detailImageUrls 는 generated OpenAPI 에 아직 미반영(drift).
// 와이어에 존재(be-funding 계약)하므로 좁은 cast 로 접근한다. vendorName 은 약한 값
// (EVERLINE 외 raw id echo)이라 화면 표시는 useVendorNameMap 으로 클라이언트측 해석한다.
const dtoVendorName = (funding: Funding): string | null =>
  (funding as { vendorName?: string | null }).vendorName ?? null;

const detailImagesOf = (funding: Funding): ReadonlyArray<string> => {
  const urls = (funding as { detailImageUrls?: ReadonlyArray<string> | null }).detailImageUrls;
  return Array.isArray(urls) ? urls.filter((u) => typeof u === 'string' && u.length > 0) : [];
};

/** 참여자가 고른 리워드 이름(settlement #9 rewardNames). */
const rewardNamesOf = (item: unknown): ReadonlyArray<string> => {
  const names = (item as { rewardNames?: ReadonlyArray<string> | null }).rewardNames;
  return Array.isArray(names) ? names.filter((n) => typeof n === 'string' && n.length > 0) : [];
};

const formatIso = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR');
};

interface OverviewProps {
  funding: Funding;
}

const Overview = ({ funding }: OverviewProps) => {
  const { t } = useTranslation('funding');
  // QA-398 — 런칭 일시 = 런칭되면 시작 일시(작성중은 비어 있음), 마감 일시 = 종료 상태로 바뀐 시각(settlement #9).
  const { launchedAt, closedAt } = funding as { launchedAt?: string | null; closedAt?: string | null };
  const vendorNames = useVendorNameMap();
  const goalUnset = isGoalUnset(funding.goalAmount);
  const rate = goalUnset ? 0 : (funding.achievementRate ?? 0);
  const clampedRate = Math.min(100, Math.max(0, rate));
  const detailImages = detailImagesOf(funding);
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <section
        className="md:col-span-2 rounded-lg p-5"
        style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
        aria-labelledby="overview-info"
      >
        <h3
          id="overview-info"
          className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('funding.detail.sectionInfo')}
        </h3>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Field
            label={t('funding.fields.title')}
            value={funding.title}
          />
          <Field
            label={t('funding.detail.fieldVendor')}
            value={vendorNames.resolve(funding.vendorId, dtoVendorName(funding))}
          />
          <Field
            label={t('funding.fields.artistId')}
            value={funding.artistName ?? '—'}
          />
          {funding.summary ? (
            <FieldFull label={t('funding.fields.summary')} value={funding.summary} />
          ) : null}
          {funding.description ? (
            <FieldHtml label={t('funding.fields.description')} html={funding.description} />
          ) : null}
        </dl>
      </section>

      <section
        className="rounded-lg p-5"
        style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
        aria-labelledby="overview-dates"
      >
        <h3
          id="overview-dates"
          className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('funding.detail.sectionDates')}
        </h3>
        <dl className="flex flex-col gap-2 text-sm">
          <Field label={t('funding.fields.startAt')} value={formatIso(funding.startAt)} />
          <Field label={t('funding.fields.endAt')} value={formatIso(funding.endAt)} />
          <Field label={t('funding.detail.launchedAt', '런칭 일시')} value={formatIso(launchedAt)} />
          <Field label={t('funding.detail.closedAt', '마감 일시')} value={formatIso(closedAt)} />
        </dl>

        <div className="mt-5 flex flex-col gap-1">
          <span className="text-xs text-[color:var(--content-muted)]">
            {t('funding.detail.achievementLabel', { rate: rate.toFixed(1) })}
          </span>
          <div
            className="h-2 w-full overflow-hidden rounded-full"
            style={{ background: 'var(--surface-1)' }}
            role="progressbar"
            aria-valuenow={Math.round(rate)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full"
              style={{ width: `${clampedRate}%`, background: 'var(--chart-primary)' }}
            />
          </div>
          <span className="text-xs text-[color:var(--content-muted)]">
            {formatMoney(funding.currentAmount)} / {goalUnset ? GOAL_UNSET_LABEL : formatMoney(funding.goalAmount)}
          </span>
          <span className="text-xs text-[color:var(--content-muted)]">
            {t('funding.detail.pledgeCountLabel', { count: funding.currentPledgeCount })}
          </span>
        </div>
      </section>

      {funding.refundPolicy ? (
        <section
          className="md:col-span-3 rounded-lg p-5"
          style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
          aria-labelledby="overview-policy"
        >
          <h3
            id="overview-policy"
            className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
          >
            {t('funding.detail.sectionPolicy')}
          </h3>
          <p className="whitespace-pre-wrap text-sm text-[color:var(--content-default)]">
            {funding.refundPolicy}
          </p>
        </section>
      ) : null}

      {detailImages.length > 0 ? (
        <section
          className="md:col-span-3 rounded-lg p-5"
          style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
          aria-labelledby="overview-detail-images"
        >
          <h3
            id="overview-detail-images"
            className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
          >
            {t('funding.detail.sectionDetailImages')}
          </h3>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {detailImages.map((url, i) => (
              <li
                key={url}
                className="overflow-hidden rounded-md"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
              >
                <img
                  src={url}
                  alt={t('funding.create.coverImageAlt') + ` ${i + 1}`}
                  className="aspect-video w-full object-cover"
                  loading="lazy"
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {funding.internalNote ? (
        <section
          className="md:col-span-3 rounded-lg p-5"
          style={{
            background: 'var(--surface-raised)',
            boxShadow: 'var(--shadow-card)',
          }}
          aria-labelledby="overview-note"
        >
          <h3
            id="overview-note"
            className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
          >
            {t('funding.detail.sectionInternalNote')}
          </h3>
          <p className="whitespace-pre-wrap text-sm text-[color:var(--content-default)]">
            {funding.internalNote}
          </p>
        </section>
      ) : null}
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-[color:var(--content-muted)]">{label}</dt>
    <dd className="text-sm text-[color:var(--content-strong)]">{value}</dd>
  </div>
);

/** 상세 본문 허용 태그 — 편집기(RichTextEditor) 출력 범위. script·이벤트 핸들러·javascript: 는 DOMPurify 가 지운다. */
const DESCRIPTION_SANITIZE = {
  ALLOWED_TAGS: ['p', 'br', 'span', 'div', 'b', 'strong', 'i', 'em', 'u', 's', 'a', 'img', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'hr', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'figure', 'figcaption', 'iframe'],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'target', 'rel', 'allowfullscreen', 'frameborder'],
};

// 메일 미리보기(emailHtmlPreview)와 같게 script·style 블록은 먼저 지운 뒤 DOMPurify 로 정리한다(이중 방어).
const sanitizeDescription = (html: string): string =>
  DOMPurify.sanitize(html.replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, ''), DESCRIPTION_SANITIZE);

/** QA-398 — 상세 본문은 편집기 HTML 이라 태그(<p> 등)가 글자로 보였다. 정리(DOMPurify)한 뒤 HTML 로 보여 준다. */
const FieldHtml = ({ label, html }: { label: string; html: string }) => (
  <div className="col-span-2 flex flex-col gap-0.5">
    <dt className="text-xs text-[color:var(--content-muted)]">{label}</dt>
    <dd
      className="rich-text-content text-sm text-[color:var(--content-strong)] [&_img]:max-w-full [&_p]:my-1"
      data-testid="funding-description"
      dangerouslySetInnerHTML={{ __html: sanitizeDescription(html) }}
    />
  </div>
);

const FieldFull = ({ label, value }: { label: string; value: string }) => (
  <div className="col-span-2 flex flex-col gap-0.5">
    <dt className="text-xs text-[color:var(--content-muted)]">{label}</dt>
    <dd className="whitespace-pre-wrap text-sm text-[color:var(--content-strong)]">
      {value}
    </dd>
  </div>
);

interface PledgesSectionProps {
  campaignId: string;
}

const PledgesSection = ({ campaignId }: PledgesSectionProps) => {
  const { t } = useTranslation('funding');
  const [pledgeStatus, setPledgeStatus] = useState<PledgeStatusEnum | ''>('');
  const [page, setPage] = useState(1);
  const query = useFundingPledgesQuery({
    campaignId,
    page,
    size: 20,
    sort: 'createdAt,desc',
    ...(pledgeStatus === '' ? {} : { status: [pledgeStatus] as const }),
  });
  const items = query.data?.items ?? [];
  const totalPages = query.data?.meta.pagination?.totalPages ?? 1;
  // QA-398 — 참여자를 이름(로그인 ID)로 — 일괄 신원 조회(user #39 loginId).
  const members = usePledgeMembers(items.map((i) => i.userId ?? '').filter(Boolean));

  return (
    <section
      aria-labelledby="pledges-heading"
      className="space-y-3 rounded-lg p-5"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <header className="flex items-center justify-between">
        <h3
          id="pledges-heading"
          className="text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('funding.pledges.sectionTitle')}
        </h3>
        <div role="radiogroup" aria-label={t('funding.pledges.columns.status')} className="flex gap-2">
          <FilterChip
            active={pledgeStatus === ''}
            label={t('funding.pledges.filterAll')}
            onClick={() => {
              setPledgeStatus('');
              setPage(1);
            }}
          />
          <FilterChip
            active={pledgeStatus === 'PAID'}
            label={t('funding.pledges.filterPaid')}
            onClick={() => {
              setPledgeStatus('PAID');
              setPage(1);
            }}
          />
          <FilterChip
            active={pledgeStatus === 'CANCELLED'}
            label={t('funding.pledges.filterCancelled')}
            onClick={() => {
              setPledgeStatus('CANCELLED');
              setPage(1);
            }}
          />
        </div>
      </header>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {t('funding.pledges.error')}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[color:var(--content-muted)]">
          {t('funding.pledges.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="pledges-list">
          {items.map((item) => (
            <li
              key={item.pledgeId}
              className="flex items-center justify-between rounded-md px-3 py-2 text-sm"
              style={{ background: 'var(--surface-1)' }}
            >
              <div className="flex flex-col gap-0.5">
                {item.userId ? (
                  <PledgeMemberButton userId={item.userId} member={members.get(item.userId)} />
                ) : (
                  <span className="text-sm text-[color:var(--content-muted)]">—</span>
                )}
                <span className="text-xs text-[color:var(--content-muted)]">
                  {formatMoney(item.pledgeAmount)} · {t('funding.pledges.columns.createdAt')} {formatIso(item.createdAt)}
                  {item.paidAt ? ` · ${t('funding.pledges.columns.paidAt')} ${formatIso(item.paidAt)}` : ''}
                </span>
                {rewardNamesOf(item).length > 0 ? (
                  <span className="text-xs text-[color:var(--content-default)]" data-testid="pledge-reward-names">
                    {t('funding.pledges.rewards', '리워드')}: {rewardNamesOf(item).join(', ')}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <PledgeStatusPill status={item.status} pledgeId={item.pledgeId} />
              </div>
            </li>
          ))}
        </ul>
      )}

      <nav
        className="flex items-center justify-end gap-2"
        aria-label={t('funding.list.paginationLabel')}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1 || query.isPending}
        >
          {t('funding.actions.prev')}
        </Button>
        <span className="text-xs text-[color:var(--content-muted)]">
          {t('funding.list.pageOf', { page, totalPages })}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || query.isPending}
        >
          {t('funding.actions.next')}
        </Button>
      </nav>
    </section>
  );
};

const FilterChip = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
    className="rounded-full border px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2"
    style={{
      background: active ? 'var(--cta-primary)' : 'var(--surface-raised)',
      color: active ? 'var(--cta-primary-on)' : 'var(--content-default)',
      borderColor: 'var(--content-muted)',
    }}
  >
    {label}
  </button>
);

export const FundingCampaignDetailPage = () => {
  const { t } = useTranslation('funding');
  const navigate = useNavigate();
  const params = useParams<{ campaignId: string }>();
  const campaignId = params.campaignId;
  const { currentAdmin } = useAuthContext();
  const roles = (currentAdmin?.roles ?? []) as ReadonlyArray<AdminRole>;
  const canWrite = canPerformFundingWrite(roles);

  const query = useFundingCampaignQuery(campaignId);
  const statsQuery = useFundingStatisticsQuery(campaignId);
  const launchMutation = useLaunchFundingCampaignMutation();
  const closeMutation = useCloseFundingCampaignMutation();
  const deleteMutation = useDeleteFundingCampaignMutation();
  const [tab, setTab] = useState<Tab>('overview');
  const [showLaunch, setShowLaunch] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const funding = query.data;
  const launchError = errorMessageOf(launchMutation.error);
  const closeError = errorMessageOf(closeMutation.error);
  const deleteError = errorMessageOf(deleteMutation.error);

  const tabs = useMemo<Array<{ id: Tab; label: string }>>(
    () => [
      { id: 'overview', label: t('funding.detail.tabOverview') },
      { id: 'rewards', label: t('funding.detail.tabRewards', { count: funding?.rewards.length ?? 0 }) },
      { id: 'pledges', label: t('funding.detail.tabPledges') },
      { id: 'statistics', label: t('funding.detail.tabStatistics') },
    ],
    [t, funding],
  );

  if (query.isPending) {
    return (
      <div role="status" aria-live="polite" className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[44px] w-full" />
        ))}
      </div>
    );
  }

  if (query.isError || !funding) {
    return (
      <section
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--status-danger-bg)',
          color: 'var(--status-danger-fg)',
        }}
        role="alert"
      >
        <p>{t('funding.detail.error')}</p>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          {t('funding.actions.retry')}
        </Button>
      </section>
    );
  }

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {funding.title}
          <FundingStatusPill status={funding.status} campaignId={funding.campaignId} />
          {funding.artistName ? (
            <span className="text-xs font-normal text-[color:var(--content-muted)]">
              {t('funding.detail.subjectWithArtist', { artistName: funding.artistName })}
            </span>
          ) : null}
        </span>
      }
      breadcrumb={[
        { label: t('funding.list.title'), href: '/funding-campaigns' },
        { label: funding.title },
      ]}
      actions={
        <div className="flex items-center gap-2">
          {/* QA-398 — 순서 [런칭] [수정] [삭제]. 수정은 작성중(DRAFT)에서만 BE 가 받아(그 외는 저장 시 거부) 그때만 보인다.
              QA-404 — [수정]은 팝업 대신 등록 폼(6단계)으로 이어서 쓴다. */}
          {canWrite && funding.status === 'DRAFT' ? (
            <Button
              type="button"
              variant="cta"
              size="sm"
              data-testid="funding-launch-button"
              onClick={() => setShowLaunch(true)}
            >
              {t('funding.actions.launch')}
            </Button>
          ) : null}
          {canWrite && funding.status === 'DRAFT' ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="funding-edit-button"
              onClick={() => navigate(`/funding-campaigns/${funding.campaignId}/edit`)}
            >
              {t('funding.actions.edit', { defaultValue: '수정' })}
            </Button>
          ) : null}
          {canWrite && funding.status === 'ACTIVE' ? (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => setShowClose(true)}
            >
              {t('funding.actions.close')}
            </Button>
          ) : null}
          {canWrite && isDeletable(funding.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="funding-delete-button"
              onClick={() => setShowDelete(true)}
            >
              {t('funding.actions.delete')}
            </Button>
          ) : null}
        </div>
      }
    >

      <nav className="flex gap-2 border-b" style={{ borderColor: 'var(--content-muted)' }}>
        {tabs.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            role="tab"
            aria-selected={tab === tabItem.id}
            data-testid={`tab-${tabItem.id}`}
            onClick={() => setTab(tabItem.id)}
            className="px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            style={{
              color:
                tab === tabItem.id
                  ? 'var(--content-strong)'
                  : 'var(--content-muted)',
              borderBottom:
                tab === tabItem.id ? '2px solid var(--cta-primary)' : '2px solid transparent',
              fontWeight: tab === tabItem.id ? 600 : 400,
            }}
          >
            {tabItem.label}
          </button>
        ))}
      </nav>

      <div role="tabpanel">
        {tab === 'overview' ? <Overview funding={funding} /> : null}
        {tab === 'rewards' ? (
          <RewardManager funding={funding} canWrite={canWrite} />
        ) : null}
        {tab === 'pledges' ? <PledgesSection campaignId={funding.campaignId} /> : null}
        {tab === 'statistics' ? (
          statsQuery.isPending ? (
            <div role="status" aria-live="polite" className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[44px] w-full" />
              ))}
            </div>
          ) : statsQuery.isError || !statsQuery.data ? (
            <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
              {t('funding.detail.error')}
            </p>
          ) : (
            <FundingStatsCard stats={statsQuery.data} />
          )
        ) : null}
      </div>

      <FundingLaunchModal
        isOpen={showLaunch}
        title={funding.title}
        isSubmitting={launchMutation.isPending}
        errorMessage={launchError}
        hasRewards={funding.rewards.length > 0}
        hasGoal={!isGoalUnset(funding.goalAmount)}
        onCancel={() => setShowLaunch(false)}
        onConfirm={(values) => {
          const note = values.note ?? '';
          launchMutation.mutate(
            {
              campaignId: funding.campaignId,
              ...(note.length > 0 ? { body: { note } } : {}),
            },
            { onSuccess: () => setShowLaunch(false) },
          );
        }}
      />

      <FundingCloseModal
        isOpen={showClose}
        title={funding.title}
        isSubmitting={closeMutation.isPending}
        errorMessage={closeError}
        onCancel={() => setShowClose(false)}
        onConfirm={(values) => {
          const body: { forceCancel: boolean; cancelReason?: string | null } = {
            forceCancel: values.forceCancel,
          };
          if (values.forceCancel) {
            body.cancelReason = values.cancelReason ?? '';
          }
          closeMutation.mutate(
            { campaignId: funding.campaignId, body },
            { onSuccess: () => setShowClose(false) },
          );
        }}
      />

      <FundingDeleteModal
        isOpen={showDelete}
        title={funding.title}
        isSubmitting={deleteMutation.isPending}
        errorMessage={deleteError}
        onCancel={() => setShowDelete(false)}
        onConfirm={() => {
          deleteMutation.mutate(funding.campaignId, {
            onSuccess: () => {
              setShowDelete(false);
              toast.success(t('funding.detail.deleteDialog.success'));
              navigate('/funding-campaigns');
            },
          });
        }}
      />

    </PageShell>
  );
};
